import { useRouter } from "next/router";
import { useEffect, useRef, VFC } from "react";

import { track } from "lib/analytics";

const SCROLL_MILESTONES = [50, 75, 100];
const TIME_MILESTONES_MS = [30_000, 60_000, 120_000, 300_000];

/**
 * Tracks scroll depth and time on page for docs analytics.
 *
 * - Scroll depth: fires "Scroll Depth Reached" at 25%, 50%, 75%, 100%
 * - Time on page: fires "Time on Page Milestone" at 30s, 60s, 120s, 300s
 *
 * Both reset on route change.
 */
export const DocsTracking: VFC = () => {
  const router = useRouter();
  const scrollMilestonesReached = useRef<Set<number>>(new Set());
  const timeMilestonesReached = useRef<Set<number>>(new Set());
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Scroll depth tracking
  useEffect(() => {
    const reached = scrollMilestonesReached.current;
    reached.clear();

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;

      if (docHeight <= 0) return;

      const scrollPercent = Math.round((scrollTop / docHeight) * 100);

      for (const milestone of SCROLL_MILESTONES) {
        if (scrollPercent >= milestone && !reached.has(milestone)) {
          reached.add(milestone);
          track("Scroll Depth Reached", {
            page_path: window.location.pathname,
            page_title: document.title,
            scroll_depth: milestone,
          });
        }
      }
    };

    // Passive listener for performance
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Check initial position (e.g., short pages or anchor links)
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [router.asPath]);

  // Time on page tracking
  useEffect(() => {
    const reached = timeMilestonesReached.current;
    reached.clear();

    // Clear any previous timers
    timers.current.forEach(clearTimeout);
    timers.current = [];

    const pageLoadTime = Date.now();

    const newTimers = TIME_MILESTONES_MS.map((ms) =>
      setTimeout(() => {
        if (!reached.has(ms)) {
          reached.add(ms);
          track("Time on Page Milestone", {
            page_path: window.location.pathname,
            page_title: document.title,
            time_on_page_seconds: ms / 1000,
          });
        }
      }, ms)
    );

    timers.current = newTimers;

    return () => {
      newTimers.forEach(clearTimeout);
    };
  }, [router.asPath]);

  return null;
};
